import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { LoadingSpinner } from '@/shared/components/ui/LoadingSpinner'
import { Header } from '@/shared/components/layout/Header'
import { StatusBar } from '@/shared/components/layout/StatusBar'
import { getSignatureByShortId } from '../services/electronicSigningApi'
import { ElectronicSignatureView, NotFound } from './ElectronicSignatureLookup'
import { DigitalSignatureView, type DigitalSignatureRecord } from './DigitalSignatureLookup'
import type { ElectronicSignatureRecord } from '../types'

/**
 * Lookup unificado por short_id (URL acortada signwiz.app/v/<shortId>).
 * Resuelve a digital o electrónica buscando en ambas tablas y renderiza
 * la vista pura correspondiente. No usa nested routers.
 */
export default function UnifiedSignatureLookup() {
  const { shortId } = useParams<{ shortId: string }>()
  const [resolved, setResolved] = useState<
    | { kind: 'electronic'; record: ElectronicSignatureRecord }
    | { kind: 'digital'; record: DigitalSignatureRecord }
    | null
  >(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!shortId) return
    getSignatureByShortId(shortId)
      .then(found => {
        if (!found) {
          setError('Firma no encontrada')
          return
        }
        if (found.kind === 'electronic') {
          setResolved({ kind: 'electronic', record: found.record })
        } else {
          setResolved({ kind: 'digital', record: found.record as unknown as DigitalSignatureRecord })
        }
      })
      .catch(e => setError(e instanceof Error ? e.message : 'Error consultando firma'))
      .finally(() => setLoading(false))
  }, [shortId])

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col pb-7 bg-legal-50 dark:bg-legal-900">
        <Header />
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
        <StatusBar />
      </div>
    )
  }
  if (error || !resolved) {
    return <NotFound message={error || 'No existe una firma con ese identificador.'} />
  }
  return resolved.kind === 'electronic'
    ? <ElectronicSignatureView record={resolved.record} />
    : <DigitalSignatureView record={resolved.record} />
}
